import moment from 'moment'
import addressValidator from '@hapi/address'
import addressValidationTlds from './tlds'
import {trim} from 'utils/stringUtils'
import * as errorMessages from './errorMessages'
import domainZones from './domainZones.json'

const PASSWORD_REGEXP = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*$/
const USERNAME_REGEXP = /^(\w|\.|_|-|\+)*$/
const BLICK_EM_GAME_NICKNAME_REGEXP = /^([a-zA-Z0-9äöüàâèéêëîïôœùûÿçß])*$/i
const BLICK_SKI_TROPHY_GAME_NICKNAME_REGEXP = /^([a-z0-9])*$/i
const NICKNAME_REGEXP = /^([a-zA-Z0-9äöüàâèéêëîïôœùûÿçß]|\.|,|_|-|@|\+)*$/i
const TWENTY_MIN_NICKNAME_REGEXP = /^([a-zA-Z0-9äöüàâèéêëîïôœùûÿçß]|\.|,|_|-|@)*$/i
const DOMAIN_ZONES_REGEXP = new RegExp(`[a-z0-9]\\.(${domainZones.join('|')})([^a-z]|$)`, 'i')
const DATE_FORMAT = 'DD.MM.YYYY'
const EMOJI_REGEXP =
  /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g

export const _required = message => value => {
  if (trim(value)) {
    return null
  }
  return message
}

export const requiredZipAndCity = (_, state) => {
  if (!trim(state.zipCode) && !trim(state.city)) {
    return errorMessages.validZipAndCity()
  }

  if (!trim(state.zipCode)) {
    return errorMessages.requireZip()
  }

  if (!trim(state.city)) {
    return errorMessages.requireCity()
  }
  return null
}

export const required = _required(errorMessages.isRequired())

export const notRequired = () => null

export const dependantRequired = (dependencyField, validator) => (value, state) => {
  if (state[dependencyField]) {
    return typeof validator === 'function' ? validator(value, state) : required(value)
  }
  return null
}

export const requiredPolicyCheckbox = client => value => {
  if (value) {
    return null
  }
  return client ? errorMessages.isRequiredClientPolicy : errorMessages.isRequiredSSOPolicy
}

export const requiredFuncMap = {
  default: required,
  zipCode: requiredZipAndCity,
  city: requiredZipAndCity,
  nickname: _required(errorMessages.requireNickname()),
  nicknameWime: _required(errorMessages.requireNickname()),
  nickname20min: _required(errorMessages.requireNickname('validation.nickname20min-required')),
}

export const minLength = length => text => {
  text = trim(text)
  return !text || text.length >= length ? null : errorMessages.minLength(length)
}

export const maxLength = length => text => {
  const trimmedText = trim(text)
  return !trimmedText || trimmedText.length <= length ? null : errorMessages.maxLength(length)
}

export const minLengthCode = length => text => {
  text = trim(text)
  return !text || text.length >= length ? null : errorMessages.minLengthCode(length)
}

export const maxLengthCode = length => text => {
  const trimmedText = trim(text)
  return !trimmedText || trimmedText.length <= length ? null : errorMessages.maxLengthCode(length)
}

export const isEmail = value =>
  // NOTE: provide tlds explicitly to fix issue with old IE
  addressValidator.email.isValid(trim(value), {allowUnicode: false, tlds: {allow: addressValidationTlds}})
    ? null
    : errorMessages.validEmail()

export const isUsername = value => {
  const trimmedValue = trim(value)
  return trimmedValue.length <= 15 && USERNAME_REGEXP.test(trimmedValue)
}

export const forbidEmoji = value => EMOJI_REGEXP.test(value) && errorMessages.forbidEmoji()

export const isValidNickname = (value, regExp = NICKNAME_REGEXP) => {
  const trimmedValue = trim(value)
  const isValid = regExp.test(trimmedValue)
  const containsLinks = DOMAIN_ZONES_REGEXP.test(trimmedValue)
  return isValid && !containsLinks
}

export const isValid20minNickname = value => isValidNickname(value, TWENTY_MIN_NICKNAME_REGEXP)

export const isValidNicknameBlickEmGame = value => {
  const trimmedValue = trim(value)
  return trimmedValue.length <= 20 && BLICK_EM_GAME_NICKNAME_REGEXP.test(trimmedValue)
}

export const isDate = value => (!value || moment(value, DATE_FORMAT, true).isValid() ? null : errorMessages.validDate())

export const isPastDate = value =>
  !value || moment(value, DATE_FORMAT).isBefore(moment()) ? null : errorMessages.dateShouldBeInPast()

export const isOldEnough = (loginCase, minAge) => value => {
  if (!['card', 'event'].includes(loginCase)) {
    return null
  }

  return moment().diff(moment(value, DATE_FORMAT), 'years') >= minAge ? null : errorMessages.validAge()
}
export const isCountryAllowed = (loginCase, allowedCountries) => value => {
  const allowed = allowedCountries.length === 0 || allowedCountries.includes(value)
  return allowed ? null : errorMessages.validCountry()
}

export const validateUsername = value => (isUsername(value) ? null : errorMessages.validUsername())

export const validPassword = message => value => {
  const trimmedValue = trim(value)
  return trimmedValue.length >= 8 && PASSWORD_REGEXP.test(trimmedValue) ? null : errorMessages.validPassword(message)
}

export const isMobileNumber = value => {
  const trimmedValue = trim(value)
  return /^\+?[\d-/\\() ]*$/.test(trimmedValue)
}

export const isVerificationCode = value => {
  const trimmedValue = trim(value)
  return /^\d+$/.test(trimmedValue)
}

export const isValidNicknameBlickSkiTrophyGame = value => {
  const trimmedValue = trim(value)
  return trimmedValue.length <= 15 && BLICK_SKI_TROPHY_GAME_NICKNAME_REGEXP.test(trimmedValue)
}

export const requiredWithMessage = message => _required(errorMessages.isRequired(message))
